import { useState, useMemo } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { adminApi } from '@/services/adminApi';
import { Button } from '@/domains/shared/components/Button';
import { Input } from '@/domains/shared/components/Input';
import { Modal } from '@/domains/shared/components/Modal';
import { Toast } from '@/domains/shared/components/Toast';
import { sanitizeForCsv } from '@/domains/shared/utils/formatters';
import type { CommercialStatus } from '@/domains/admin/types';
import type { AdminLeadItem } from '@/services/adminApi';

export function AdminLeadsPage() {
  const { navigate } = useRouter();
  const { leads, isLeadsLoading, logout, updateLeadStatus, updateLeadNote } = useAdminAuth();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [onlyRetentionRisk, setOnlyRetentionRisk] = useState<boolean>(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<'overview' | 'timeline'>('overview');
  const [leadNoteInput, setLeadNoteInput] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Lead selecionado dinâmico do estado global de leads para manter histórico sincronizado
  const selectedLead = useMemo(() => {
    return leads.find((l) => l.id === selectedLeadId) || null;
  }, [leads, selectedLeadId]);

  // Filtros combinados em memória
  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      const matchesSearch =
        lead.name.toLowerCase().includes(search.toLowerCase()) ||
        lead.email.toLowerCase().includes(search.toLowerCase()) ||
        lead.companyName.toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === 'all' || lead.commercialStatus === statusFilter;
      const matchesTier = tierFilter === 'all' || lead.overallTierKey === tierFilter;
      const matchesRetention = !onlyRetentionRisk || lead.hasRetentionRisk;

      return matchesSearch && matchesStatus && matchesTier && matchesRetention;
    });
  }, [leads, search, statusFilter, tierFilter, onlyRetentionRisk]);

  const handleOpenLead = (lead: AdminLeadItem) => {
    setSelectedLeadId(lead.id);
    setLeadNoteInput(lead.notes || '');
    setModalTab('overview');
    adminApi.recordAuditLog({
      action: 'view_lead',
      resourceType: 'submission',
      resourceId: lead.id,
    }).catch(() => {});
  };

  const handleSaveNote = async () => {
    if (!selectedLead) return;
    try {
      await updateLeadNote(selectedLead.id, leadNoteInput);
      adminApi.recordAuditLog({
        action: 'update_note',
        resourceType: 'lead_status_history',
        resourceId: selectedLead.id,
      }).catch(() => {});
      setToastMessage('Anotação comercial salva e registrada na timeline!');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar anotação.';
      setToastMessage(`Erro: ${msg}`);
    }
  };

  const handleStatusChange = async (leadId: string, leadName: string, newStatus: CommercialStatus) => {
    try {
      await updateLeadStatus(leadId, newStatus);
      adminApi.recordAuditLog({
        action: 'update_status',
        resourceType: 'submission',
        resourceId: leadId,
        metadata: { newStatus },
      }).catch(() => {});
      setToastMessage(`Status de "${leadName}" atualizado para "${newStatus}" e registrado no histórico.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao atualizar status.';
      setToastMessage(`Erro: ${msg}`);
    }
  };

  const handleExportCsv = () => {
    // Sanitização rigorosa contra CSV Formula Injection
    const headers = [
      'ID',
      'Nome',
      'Email',
      'Telefone',
      'Empresa',
      'Score',
      'Tier',
      'Status',
      'RiscoRetencao',
      'Origem',
      'Campanha',
      'Data',
    ];
    const rows = filteredLeads.map((l) => [
      sanitizeForCsv(l.id),
      sanitizeForCsv(l.name),
      sanitizeForCsv(l.email),
      sanitizeForCsv(l.phone),
      sanitizeForCsv(l.companyName),
      l.overallScore.toString(),
      sanitizeForCsv(l.overallTierKey),
      sanitizeForCsv(l.commercialStatus),
      l.hasRetentionRisk ? 'SIM' : 'NAO',
      sanitizeForCsv(l.origin || 'direto'),
      sanitizeForCsv(l.utmCampaign || 'geral'),
      sanitizeForCsv(l.createdAt),
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(';'), ...rows.map((e) => e.join(';'))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `leads_diagnostico_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    adminApi.recordAuditLog({
      action: 'export_csv',
      resourceType: 'submission',
      metadata: { total: filteredLeads.length },
    }).catch(() => {});

    setToastMessage(`Exportação concluída: ${filteredLeads.length} leads exportados com sanitização!`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-navy text-brand-offWhite">
      {/* Header Administrativo */}
      <header className="border-b border-white/10 bg-navy-dark/90 px-4 py-3.5 sticky top-0 z-40 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-orange text-sm font-black text-white">
              IC
            </div>
            <div>
              <span className="block text-[10px] font-extrabold uppercase tracking-wider text-brand-orange">
                DecolaCont Admin
              </span>
              <span className="block text-xs font-bold text-white sm:text-sm">
                Gestão de Leads Qualificados
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <nav className="flex items-center gap-1 bg-white/5 p-1 rounded-xl border border-white/10">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-offWhite/80 hover:text-white"
              >
                Visão Geral
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-orange text-white"
              >
                Leads ({leads.length})
              </button>
            </nav>

            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await logout();
                navigate('/admin/login');
              }}
              className="text-xs text-brand-offWhite/60 hover:text-feedback-danger"
            >
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white sm:text-3xl">Base de Leads</h1>
            <p className="mt-1 text-xs text-brand-offWhite/70 sm:text-sm">
              Visualize, filtre, altere o status comercial e acompanhe o histórico de cada oportunidade.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleExportCsv}>
              ↓ Exportar CSV ({filteredLeads.length})
            </Button>
          </div>
        </div>

        {/* Barra de Filtros */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-navy-light/40 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Buscar por Nome, E-mail ou Empresa"
              placeholder="Digite para filtrar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="flex flex-col gap-1.5 text-left">
              <label className="text-xs font-semibold uppercase tracking-wider text-brand-offWhite/80">
                Status Comercial
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-navy-light/60 px-4 py-3 text-sm text-white shadow-inner focus:border-brand-orange focus:outline-none min-h-[44px]"
              >
                <option value="all" className="bg-navy">Todos os status</option>
                <option value="novo" className="bg-navy">Novo</option>
                <option value="em_contato" className="bg-navy">Em contato</option>
                <option value="qualificado" className="bg-navy">Qualificado</option>
                <option value="sem_interesse" className="bg-navy">Sem interesse</option>
                <option value="convertido" className="bg-navy">Convertido</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5 text-left">
              <label className="text-xs font-semibold uppercase tracking-wider text-brand-offWhite/80">
                Faixa de Maturidade (Tier)
              </label>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-navy-light/60 px-4 py-3 text-sm text-white shadow-inner focus:border-brand-orange focus:outline-none min-h-[44px]"
              >
                <option value="all" className="bg-navy">Todos os tiers</option>
                <option value="housekeeping" className="bg-navy">Precisamos arrumar a casa (0-24)</option>
                <option value="scattered" className="bg-navy">Presença dispersa (25-44)</option>
                <option value="building" className="bg-navy">Presença em construção (45-64)</option>
                <option value="conversations" className="bg-navy">Presença que abre conversas (65-79)</option>
                <option value="structured" className="bg-navy">Presença bem estruturada (80-100)</option>
              </select>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-brand-offWhite/90 select-none">
              <input
                type="checkbox"
                checked={onlyRetentionRisk}
                onChange={(e) => setOnlyRetentionRisk(e.target.checked)}
                className="h-4 w-4 rounded border-white/20 bg-navy text-brand-orange focus:ring-brand-orange"
              />
              <span className="text-feedback-danger font-semibold">
                Filtrar apenas leads com Risco de Retenção (Experiência &lt; 40%)
              </span>
            </label>

            <span className="text-xs text-brand-offWhite/60">
              Exibindo <strong>{filteredLeads.length}</strong> de <strong>{leads.length}</strong> leads
            </span>
          </div>
        </div>

        {/* Tabela de Leads */}
        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-navy-light/30">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-white/10 bg-white/5 text-[11px] uppercase tracking-wider text-brand-offWhite/70">
              <tr>
                <th className="px-4 py-3.5">Lead / Contato</th>
                <th className="px-4 py-3.5">Escritório</th>
                <th className="px-4 py-3.5 text-center">Score Geral</th>
                <th className="px-4 py-3.5">Gargalo / Alerta</th>
                <th className="px-4 py-3.5">Origem / Campanha</th>
                <th className="px-4 py-3.5">Status Comercial</th>
                <th className="px-4 py-3.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3.5 font-medium text-white">
                    <div className="font-semibold text-sm">{lead.name}</div>
                    <div className="text-[11px] text-brand-offWhite/70">{lead.phone}</div>
                    <div className="text-[11px] text-brand-offWhite/50">{lead.email}</div>
                  </td>
                  <td className="px-4 py-3.5 text-brand-offWhite/90 font-medium">
                    {lead.companyName}
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <span className="inline-block px-2.5 py-1 rounded-lg bg-brand-orange/20 font-black text-brand-orange text-sm">
                      {lead.overallScore}%
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="capitalize font-medium text-white">
                      {lead.primaryPriorityKey.replace('_', ' ')}
                    </div>
                    {lead.hasRetentionRisk && (
                      <span className="inline-block mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold bg-feedback-danger/20 text-feedback-danger">
                        Risco de Retenção
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-brand-offWhite/70 text-[11px]">
                    <div>{lead.origin || 'direto'}</div>
                    <div className="text-white/40">{lead.utmCampaign || 'orgânico'}</div>
                  </td>
                  <td className="px-4 py-3.5">
                    <select
                      value={lead.commercialStatus}
                      onChange={(e) =>
                        handleStatusChange(lead.id, lead.name, e.target.value as CommercialStatus)
                      }
                      className="rounded-lg border border-white/10 bg-navy px-2.5 py-1.5 text-xs font-semibold text-white focus:outline-none focus:border-brand-orange"
                    >
                      <option value="novo">Novo</option>
                      <option value="em_contato">Em contato</option>
                      <option value="qualificado">Qualificado</option>
                      <option value="sem_interesse">Sem interesse</option>
                      <option value="convertido">Convertido</option>
                    </select>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenLead(lead)}
                    >
                      Ver Detalhes
                    </Button>
                  </td>
                </tr>
              ))}

              {filteredLeads.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-brand-offWhite/60">
                    {isLeadsLoading
                      ? 'Carregando leads cadastrados...'
                      : 'Nenhum lead encontrado com os filtros aplicados.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Modal de Detalhes e Timeline do Lead */}
      {selectedLead && (
        <Modal
          isOpen={Boolean(selectedLead)}
          onClose={() => setSelectedLeadId(null)}
          title={`Lead: ${selectedLead.name}`}
          description={`Escritório ${selectedLead.companyName} • Status atual: ${selectedLead.commercialStatus}`}
          maxWidth="lg"
          footer={
            <>
              <Button variant="secondary" onClick={() => setSelectedLeadId(null)}>
                Fechar
              </Button>
              {modalTab === 'overview' && (
                <Button variant="primary" onClick={handleSaveNote}>
                  Salvar Anotação
                </Button>
              )}
            </>
          }
        >
          <div className="space-y-4 text-left">
            {/* Navegação entre Abas do Modal */}
            <div className="flex border-b border-white/10 gap-2 pb-2">
              <button
                type="button"
                onClick={() => setModalTab('overview')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  modalTab === 'overview'
                    ? 'bg-brand-orange text-white'
                    : 'text-brand-offWhite/70 hover:text-white bg-white/5'
                }`}
              >
                Visão Geral do Diagnóstico
              </button>
              <button
                type="button"
                onClick={() => setModalTab('timeline')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
                  modalTab === 'timeline'
                    ? 'bg-brand-orange text-white'
                    : 'text-brand-offWhite/70 hover:text-white bg-white/5'
                }`}
              >
                <span>Timeline de Alterações</span>
                <span className="rounded-full bg-white/20 px-1.5 py-0.2 text-[10px]">
                  {selectedLead.history?.length || 0}
                </span>
              </button>
            </div>

            {/* ABA 1: VISÃO GERAL */}
            {modalTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-xs bg-white/5 p-3 rounded-xl">
                  <div>
                    <span className="text-brand-offWhite/60 block">WhatsApp:</span>
                    <span className="font-semibold text-white">{selectedLead.phone}</span>
                  </div>
                  <div>
                    <span className="text-brand-offWhite/60 block">E-mail:</span>
                    <span className="font-semibold text-white">{selectedLead.email}</span>
                  </div>
                  <div>
                    <span className="text-brand-offWhite/60 block">Nota Geral:</span>
                    <span className="font-black text-brand-orange">{selectedLead.overallScore}%</span>
                  </div>
                  <div>
                    <span className="text-brand-offWhite/60 block">Prioridade Principal:</span>
                    <span className="font-semibold text-white capitalize">
                      {selectedLead.primaryPriorityKey.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                {/* Notas das 6 dimensões */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-white mb-2">
                    Notas por Dimensão Estratégica
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(selectedLead.categoryScores).map(([k, val]) => (
                      <div key={k} className="flex justify-between bg-white/5 px-2.5 py-1.5 rounded-lg">
                        <span className="capitalize text-brand-offWhite/80">{k.replace('_', ' ')}</span>
                        <span className="font-bold text-brand-orange">{val}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Alteração Rápida de Status */}
                <div className="bg-white/5 p-3 rounded-xl">
                  <label className="text-xs font-semibold uppercase tracking-wider text-white block mb-1.5">
                    Alterar Status Comercial
                  </label>
                  <select
                    value={selectedLead.commercialStatus}
                    onChange={(e) =>
                      handleStatusChange(selectedLead.id, selectedLead.name, e.target.value as CommercialStatus)
                    }
                    className="w-full rounded-lg border border-white/10 bg-navy px-3 py-2 text-xs font-semibold text-white focus:outline-none focus:border-brand-orange"
                  >
                    <option value="novo">Novo</option>
                    <option value="em_contato">Em contato</option>
                    <option value="qualificado">Qualificado</option>
                    <option value="sem_interesse">Sem interesse</option>
                    <option value="convertido">Convertido</option>
                  </select>
                </div>

                {/* Campo de Anotações Comerciais */}
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-white block mb-1">
                    Anotações Comerciais (Israel & Time)
                  </label>
                  <textarea
                    rows={3}
                    value={leadNoteInput}
                    onChange={(e) => setLeadNoteInput(e.target.value)}
                    placeholder="Insira detalhes da conversa, riscos ou próximos passos..."
                    className="w-full rounded-xl border border-white/10 bg-navy-light/60 p-3 text-xs text-white placeholder-brand-offWhite/40 focus:border-brand-orange focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* ABA 2: TIMELINE / HISTÓRICO DE ALTERAÇÕES */}
            {modalTab === 'timeline' && (
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {selectedLead.history && selectedLead.history.length > 0 ? (
                  <div className="relative border-l-2 border-brand-orange/40 ml-2 space-y-4 py-1">
                    {selectedLead.history.map((entry) => (
                      <div key={entry.id} className="relative pl-5 text-xs">
                        {/* Marcador da timeline */}
                        <div className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-brand-orange" />

                        <div className="flex items-center justify-between text-[11px] text-brand-offWhite/60">
                          <span className="font-bold text-brand-orange">{entry.changedBy}</span>
                          <span>{new Date(entry.timestamp || entry.createdAt).toLocaleString('pt-BR')}</span>
                        </div>

                        <div className="mt-1 bg-white/5 p-2.5 rounded-lg border border-white/5">
                          {entry.field === 'commercial_status' ? (
                            <div>
                              <span className="text-brand-offWhite/70">Alteração de status: </span>
                              {entry.oldValue ? (
                                <span>
                                  <span className="line-through text-brand-offWhite/50">{entry.oldValue}</span>
                                  {' → '}
                                  <strong className="text-white uppercase">{entry.newValue}</strong>
                                </span>
                              ) : (
                                <strong className="text-white uppercase">{entry.newValue}</strong>
                              )}
                            </div>
                          ) : (
                            <div>
                              <span className="text-brand-offWhite/70 block mb-0.5">Anotação atualizada:</span>
                              <p className="text-white italic">"{entry.newValue}"</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-xs text-brand-offWhite/60">
                    Nenhum registro de alteração para este lead ainda.
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
